import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { verifyPaymentStatus } from '../services/paymentService';
import { useWallet } from '../context/WalletContext';

const PaymentReturn = () => {
  const navigate = useNavigate();
  const { refreshWallet } = useWallet();
  const [status, setStatus] = useState('verifying');
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);
  const pollCountRef = useRef(0);

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        // Get charge ID from localStorage (stored before redirect)
        const chargeId = localStorage.getItem('pending_charge_id');

        if (!chargeId) {
          throw new Error('No payment information found. Please contact support if you completed a payment.');
        }

        // Verify payment with backend
        const response = await verifyPaymentStatus(parseInt(chargeId));

        setPaymentData(response);
        
        // Map backend statuses to UI states
        const backendStatus = (response.status || '').toLowerCase();
        let mappedStatus = backendStatus;
        
        if (['paid', 'completed', 'succeeded', 'success'].includes(backendStatus)) {
          mappedStatus = 'completed';
          // Clear localStorage on successful payment
          localStorage.removeItem('pending_charge_id');
          // Credits may have moved — refresh the balance shown in the navbar
          refreshWallet();
        } else if (['pending', 'processing'].includes(backendStatus)) {
          mappedStatus = 'pending';
          // Poll again in 3 seconds if still pending (max 10 times = 30 seconds)
          if (pollCountRef.current < 10) {
            pollCountRef.current += 1;
            setTimeout(() => {
              verifyPayment();
            }, 3000);
          } else {
            // After max polls, still show pending but stop polling
            mappedStatus = 'pending';
          }
        } else if (['failed', 'declined', 'rejected'].includes(backendStatus)) {
          mappedStatus = 'failed';
          localStorage.removeItem('pending_charge_id');
        } else if (['cancelled', 'canceled'].includes(backendStatus)) {
          mappedStatus = 'cancelled';
          localStorage.removeItem('pending_charge_id');
        } else if (['refunded'].includes(backendStatus)) {
          mappedStatus = 'refunded';
          localStorage.removeItem('pending_charge_id');
        }
        
        setStatus(mappedStatus);
      } catch (err) {
        console.error('Payment verification error:', err);
        setError(err.message || 'Failed to verify payment');
        setStatus('error');
        localStorage.removeItem('pending_charge_id');
      }
    };

    verifyPayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContinue = () => {
    // A top-up that paid off a booking belongs on the dashboard with the
    // booking; a plain top-up belongs back on the wallet.
    const isPlainTopUp =
      paymentData?.charge_type === 'credit_topup' &&
      paymentData?.settlement?.status !== 'paid';

    navigate(isPlainTopUp ? '/wallet' : '/owner/dashboard');
  };

  // The verify endpoint returns amounts already in rands (e.g. "250.00"),
  // not cents — don't divide.
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR'
    }).format(Number(value || 0));
  };

  const isTopUp = paymentData?.charge_type === 'credit_topup';
  // Set when the top-up was raised to pay for a booking in one step.
  const settlement = paymentData?.settlement;
  const settledBooking = settlement?.status === 'paid';

  // Voice: calm certainty. The user just paid money and got redirected —
  // this page's only job is to say, plainly, what happened.
  const successHeading = () => {
    if (settledBooking) return 'Booking paid. Sorted.';
    if (isTopUp) return 'Credits added. Sorted.';
    return 'Paid. Sorted.';
  };

  const successMessage = () => {
    const credits = Number(
      paymentData?.credits || paymentData?.amount || 0,
    ).toFixed(2);

    if (settledBooking) {
      return `We loaded ${credits} credits and paid for your booking with them.`;
    }
    if (isTopUp && settlement?.status === 'insufficient') {
      return `${credits} credits were added, but your booking still needs payment — the total changed while you were paying.`;
    }
    if (isTopUp) {
      return `${credits} credits are now available in your wallet.`;
    }
    return 'Your payment has been processed successfully.';
  };

  // The 2b state pattern: icon circle + heading + one reassurance line +
  // one primary action. 60% register — keylines only, no offset shadows,
  // sentence case. Same tokens as everywhere else, lower volume.
  const IconCircle = ({ bg, children }) => (
    <Box
      sx={{
        width: 72,
        height: 72,
        borderRadius: '50%',
        border: '2.5px solid #191512',
        backgroundColor: bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mx: 'auto',
        mb: 3,
      }}
    >
      {children}
    </Box>
  );

  // Strip the theme's pressable offset-shadow: quiet register.
  const quietButton = { boxShadow: 'none', '&:active': { boxShadow: 'none', transform: 'none' } };
  const monoSx = { fontFamily: '"IBM Plex Mono", monospace', fontWeight: 600, fontVariantNumeric: 'tabular-nums' };

  const renderContent = () => {
    if (status === 'verifying') {
      return (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress size={56} color="secondary" sx={{ mb: 3 }} />
          <Typography variant="h4" sx={{ mb: 1 }}>
            Checking with the bank…
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Hang ten — this takes a few seconds. Don’t close the page.
          </Typography>
        </Box>
      );
    }

    if (status === 'completed' || status === 'succeeded') {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <IconCircle bg="#C9F0D8">
            <CheckCircle size={40} color="#0A5C33" />
          </IconCircle>
          <Typography variant="h4" sx={{ mb: 1 }}>
            {successHeading()}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {successMessage()}
          </Typography>

          {settlement?.status === 'insufficient' && (
            <Alert severity="warning" sx={{ maxWidth: 500, mx: 'auto', mb: 3 }}>
              Your credits are safe in your wallet. Head back to the booking to
              finish paying.
            </Alert>
          )}

          {paymentData && (
            <Card sx={{ maxWidth: 400, mx: 'auto', mb: 3, textAlign: 'left' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Amount paid
                  </Typography>
                  <Typography sx={monoSx}>
                    {formatCurrency(paymentData.amount)}
                  </Typography>
                </Box>
                {isTopUp && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Credits received
                    </Typography>
                    <Typography sx={monoSx}>
                      {Number(paymentData.credits || paymentData.amount).toFixed(2)}
                    </Typography>
                  </Box>
                )}
                {paymentData.transaction_id && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Transaction ID
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                      {paymentData.transaction_id}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Paid with
                  </Typography>
                  <Typography variant="body2">Stitch</Typography>
                </Box>
              </CardContent>
            </Card>
          )}

          <Button
            variant="contained"
            size="large"
            onClick={handleContinue}
            sx={{
              ...quietButton,
              backgroundColor: '#191512',
              border: 'none',
              '&:hover': { backgroundColor: '#332C26', boxShadow: 'none' },
            }}
          >
            {isTopUp && !settledBooking ? 'View wallet' : 'View booking'}
          </Button>
        </Box>
      );
    }

    if (status === 'pending' || status === 'processing') {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <IconCircle bg="#FFE9A8">
            <AlertCircle size={40} color="#8C5A00" />
          </IconCircle>
          <Typography variant="h4" sx={{ mb: 1 }}>
            Still processing
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 440, mx: 'auto' }}>
            The bank is taking its time — nothing is lost. We’ll email you
            the moment it clears, and your booking status will update on its
            own.
          </Typography>
          <Button variant="outlined" onClick={handleContinue} sx={quietButton}>
            Go to dashboard
          </Button>
        </Box>
      );
    }

    if (status === 'cancelled') {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <IconCircle bg="#EFE9E1">
            <XCircle size={40} color="#75695E" />
          </IconCircle>
          <Typography variant="h4" sx={{ mb: 1 }}>
            Payment cancelled
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            You backed out on the payment page. Nothing was charged.
          </Typography>
          <Button variant="outlined" onClick={handleContinue} sx={quietButton}>
            Back to dashboard
          </Button>
        </Box>
      );
    }

    // Failed or error. Voice: what happened, what it cost (nothing), what to
    // do next. Never "Oops!".
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <IconCircle bg="#FFDCD2">
          <XCircle size={40} color="#B02A0E" />
        </IconCircle>
        <Typography variant="h4" sx={{ mb: 1, color: 'error.main' }}>
          That didn’t go through
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 440, mx: 'auto' }}>
          Nothing was taken off your card and your booking is safe. Give it
          another go.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ maxWidth: 500, mx: 'auto', mb: 3, textAlign: 'left' }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => window.location.reload()}
            sx={quietButton}
          >
            Try again
          </Button>
          <Button variant="outlined" onClick={handleContinue} sx={quietButton}>
            Get help
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Card>
        <CardContent>
          {renderContent()}
        </CardContent>
      </Card>
    </Container>
  );
};

export default PaymentReturn;
